import React, { Fragment, useState, useMemo, useEffect } from "react"
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@material-ui/core"
import ExpenseTable from "../../Common/ExpenseTable/index"
import { useDispatch } from "react-redux"
import NoteBox from "../../Common/NoteBox/index"
import ExpenseTableTotal from "../../Common/ExpenseTableTotal/index"
import {
  clientColumns,
  clientColumnsWithDiscount,
  mobileClientColumns,
} from "../../../../pages/Shoot/pages/Billing/BillingContext"
import HeaderByLocation from "../../Common/HeaderByLocation/index"
import { toggleFlaggingDialog } from "../../../../actions/billingDialogs"
import { openSnackBar } from "../../../../actions/snack-bar"
import { updateFlagStatusCrewClient } from "../../../../actions/billing-crew-client"
import calculateTotals from "../../../../utils/calculateTotals"
import markUpCalc from "../../../../utils/markUpCalc"
import PropTypes from "prop-types"
import { isEmpty } from "lodash"
import useStyles from "./styles"

const EstimateCardBillingClient = ({
  dealMemoData = {},
  setDealMemoData = () => { },
  estimateCrewArray = [],
  checkDiscount = false,
  ...props
}) => {
  const [noteVal, setNoteVal] = useState("")
  const [cardData, setCardData] = useState([])
  const dispatch = useDispatch()
  const classes = useStyles()

  const theme = useTheme()
  const mobileView = useMediaQuery(theme.breakpoints.only("xs"))
  const desktopView = useMediaQuery(theme.breakpoints.up("md"))

  const discount = dealMemoData && dealMemoData.discount
  const depositPaidAmount =
    Boolean(dealMemoData.deposit_required) && Boolean(dealMemoData.first_paid)
      ? dealMemoData.first_half_amount
      : 0

  // set note val on page load if anything is there
  useEffect(
    () => {
      if (dealMemoData && dealMemoData.estimate_note) {
        setNoteVal(dealMemoData.estimate_note)
      }
    },
    [dealMemoData]
  )

  const amounts = useMemo(
    () => {
      if (dealMemoData) {
        return calculateTotals(estimateCrewArray, depositPaidAmount, discount)
      }
    },
    [estimateCrewArray, depositPaidAmount, discount]
  )

  useEffect(
    () => {
      setCardData(estimateCrewArray)
    },
    [estimateCrewArray]
  )

  // *** RESOLVE FLAG  ***

  const setItemToFlag = (nextStatusObj, expenseId) => {
    // statusCodeObj, itemId is the expense_id of the chosen expense
    let clientFlagStatus = {
      client_flag_status_id: nextStatusObj.client_flag_status_id,
      client_flag_comment: nextStatusObj.comment,
    }
    // args come from Expense Table handleFlag fn, not from within this file
    dispatch(updateFlagStatusCrewClient(clientFlagStatus, expenseId))
      .then(() => {
        let newStatusString =
          nextStatusObj.client_flag_status_id === 4 ? "flagged" : "accepted"
        dispatch(
          openSnackBar({
            message:
              newStatusString === "flagged"
                ? "Expense item flagged"
                : "Flag removed successfully",
          })
        )
        setCardData(_prevState => {
          let newState = _prevState.map(user => {
            user.expenses = user.expenses.map(expense => {
              if (expense.expense_id === expenseId) {
                //this is the expense that needs its flag_status changed
                expense.client_flag_status = newStatusString
                expense.client_flag_comment =
                  clientFlagStatus.client_flag_comment
              }
              return expense
            })
            return user
          })
          return newState
        })
      })
      .catch(err => {
        console.log(err)
        dispatch(openSnackBar({ message: "Flag update was unsuccessful" }))
      })
  }

  const handleFlagDialog = (nextStatus, item) => {
    // statusCodeObj { status_id: #}, itemId is the expense_id of the chosen expense
    // args come from Expense Table handleFlag fn, not from within this file
    const flagObj = {
      flagFunc: setItemToFlag,
      nextStatus: nextStatus,
      comment: item.client_flag_comment,
      funcParams: [item.expense_id],
    }
    dispatch(toggleFlaggingDialog(true, flagObj))
  }
  // *** RESOLVE FLAG END ***

  if (isEmpty(dealMemoData)) {
    return (
      <Box
        mt={4}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Typography variant="h2" style={{ marginBottom: "25px" }}>
          No Estimate Currently Available for Review
        </Typography>
      </Box>
    )
  }

  const estimateColumns = useMemo(
    () => {
      if (checkDiscount && !mobileView) {
        return clientColumnsWithDiscount
      } else if (!checkDiscount && !mobileView) {
        return clientColumns
      } else {
        return mobileClientColumns
      }
    },
    [checkDiscount, mobileView]
  )

  return (
    <Card style={{ padding: "15px" }}>
      <CardContent>
        <HeaderByLocation
          headerInfo={dealMemoData}
          urlMatch={props.match.params}
        />
        <ExpenseTable
          columns={estimateColumns}
          data={cardData.length > 0 ? cardData : estimateCrewArray}
        >
          <ExpenseTable.Header />
          <ExpenseTable.RoleGroup>
            <ExpenseTable.RoleBar hideCrewNames={false} />
            <ExpenseTable.ItemsGroup
              style={{ marginLeft: desktopView ? 30 : 0 }}
            >
              <ExpenseTable.ItemBar>
                {/* name piece */}
                <ExpenseTable.NameItem
                  style={
                    mobileView
                      ? {
                        gridColumn: "1/span 6",
                      }
                      : {}
                  }
                />
                {/* quantity input  */}
                {desktopView ? (
                  <ExpenseTable.NumberItem valueForProperty="quantity" />
                ) : (
                  <Box
                    style={
                      mobileView
                        ? {
                          gridRow: "3/3",
                          gridColumn: "1/1",
                        }
                        : {
                          gridRow: "1/1",
                          gridColumn: "2/2",
                        }
                    }
                  >
                    {mobileView && (
                      <Typography className={classes.mobileHeaders}>
                        Quantity
                      </Typography>
                    )}
                    <ExpenseTable.NumberItem valueForProperty="quantity" />
                  </Box>
                )}
                {/* quantity input ends  */}
                {/* rate begins */}
                <Box
                  style={
                    mobileView
                      ? {
                        gridRow: "3/3",
                        gridColumn: "2/2",
                      }
                      : {
                        gridRow: "1/1",
                        gridColumn: "3/3",
                      }
                  }
                >
                  {mobileView && (
                    <Typography className={classes.mobileHeaders}>
                      Rate($)
                    </Typography>
                  )}
                  <ExpenseTable.CalculatedItem>
                    {values => {


                      return values.is_percentage
                        ? (
                          Number(
                            values.actual_rate * (values.client_rate / 100)
                          ) + Number(values.actual_rate)
                        ).toFixed(2)
                        : (
                          (values.quantity > 0
                            ? Number(values.client_rate / values.quantity)
                            : Number(values.client_rate)) +
                          Number(values.actual_rate)
                        ).toFixed(2)
                    }}
                  </ExpenseTable.CalculatedItem>
                </Box>
                {/* rate ends */}
                {/* discount begins */}
                <Fragment>
                  {/* Only show discount if a discount exists  in webview */}
                  {checkDiscount &&
                    !mobileView && (
                      <ExpenseTable.NumberItem valueForProperty="discount" />
                    )}

                  {/* Only show discount if a discount exists and its in mobile view */}
                  {checkDiscount &&
                    mobileView && (
                      <Box
                        style={{
                          gridRow: "3/3",
                          gridColumn: "3/3",
                          justifyContent: "center",
                        }}
                      >
                        <Typography className={classes.mobileHeaders}>
                          Discount($)
                        </Typography>
                        <ExpenseTable.NumberItem valueForProperty="discount" />
                      </Box>
                    )}
                  {/* discount ends */}
                  {/* total starts */}
                  <Box
                    style={
                      mobileView ? { gridRow: "3/3", gridColumn: "4/4" } : {}
                    }
                  >
                    {mobileView && (
                      <Typography className={classes.mobileHeaders}>
                        Total($)
                      </Typography>
                    )}
                    <ExpenseTable.TotalItem>
                      {values =>
                        values.is_percentage
                          ? (
                            Number(values.actual_rate) *
                            Number(values.quantity) +
                            markUpCalc(
                              values.quantity * values.actual_rate,
                              values.client_rate
                            ) -
                            Number(values.discount)
                          ).toFixed(2)
                          : (
                            (values.quantity > 0
                              ? Number(values.client_rate)
                              : 0) +
                            (Number(values.actual_rate) *
                              Number(values.quantity) -
                              Number(values.discount))
                          ).toFixed(2)
                      }
                    </ExpenseTable.TotalItem>
                  </Box>
                  {/* total ends */}
                </Fragment>
                <ExpenseTable.Item
                  valueForProperty="description"
                  style={
                    !mobileView
                      ? {
                        gridRow: "2/2",
                        gridColumn: "1/ span 6",
                      }
                      : {
                        gridRow: "2/3",
                        gridColumn: "1/ span 6",
                      }
                  }
                />

                <ExpenseTable.FlagButton onClick={handleFlagDialog} />
              </ExpenseTable.ItemBar>
            </ExpenseTable.ItemsGroup>
          </ExpenseTable.RoleGroup>
        </ExpenseTable>
        <Box
          display={!mobileView ? "flex" : ""}
          justifyContent={!mobileView ? "space-between" : ""}
          style={{ marginTop: "40px" }}
        >
          <Box width={!mobileView ? "50%" : "100%"}>
            <NoteBox
              noteVal={noteVal}
              setNoteVal={setNoteVal}
              isDisabled
              {...props}
            />
          </Box>
          <Box
            width={!mobileView ? "50%" : "100%"}
            marginLeft={!mobileView ? "20%" : ""}
            marginRight={!mobileView ? "20px" : ""}
            flexShrink={1}
          >
            <ExpenseTableTotal data={amounts} discount={discount} client={true} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

EstimateCardBillingClient.propTypes = {
  dealMemoData: PropTypes.object,
  setDealMemoData: PropTypes.func,
  estimateCrewArray: PropTypes.array,
  checkDiscount: PropTypes.bool,
}

export default EstimateCardBillingClient
